/**
 * Debounced autosave (spec §14, milestone 7.3).
 *
 * The service knows nothing about what a site is or where it is stored: callers report that the
 * document changed, and it decides *when* to run the save callback they gave it. That split is what
 * keeps a continuous edit — a maturity slider dragged for five seconds, a paint stroke — from
 * writing a document per frame, while still guaranteeing the write lands.
 *
 * Two clocks govern a save. The debounce collapses a burst of changes into one write once the burst
 * stops; `maxWaitMs` is the ceiling on how long an *unbroken* stream of changes may defer it, so a
 * drag that never pauses is still persisted while it happens rather than only when it ends.
 *
 * Saves never overlap. Changes arriving while a write is in flight re-arm the timer afterwards, so
 * the store sees them in order and the last write always reflects the latest state.
 *
 * A caller that can tell the difference between a document worth writing and one that is not — an
 * untitled, empty site — says so through `shouldSave`, which is asked at the last moment before each
 * write rather than at `markDirty`, because that is when the answer is actually known.
 */

export type AutosavePhase =
  /** No unsaved changes and nothing has been written this session. */
  | 'clean'
  /** Changes are waiting for the debounce to elapse. */
  | 'pending'
  /** A write is in flight. */
  | 'saving'
  /** Everything is written. */
  | 'saved'
  /** The last write failed; the changes are still unsaved. */
  | 'error';

export interface AutosaveStatus {
  phase: AutosavePhase;
  /** Epoch milliseconds of the last successful save, or null if none has succeeded yet. */
  savedAt: number | null;
  /** Message from the last failed save, cleared by the next success. */
  error: string | null;
}

export interface AutosaveServiceDeps {
  /** Persist the current document. Rejecting leaves the document dirty. */
  save: () => Promise<unknown>;
  /**
   * Asked immediately before each write. Returning false drops the pending change instead of
   * saving it — for a document there is no point keeping, such as an untitled site with nothing in
   * it. Dropping rather than deferring is deliberate: the next write snapshots the whole document
   * anyway, so nothing declined here can be lost, and holding the change would leave the unload
   * guard warning about work that is not worth saving.
   */
  shouldSave?: () => boolean;
  /** Quiet period after the last change before a save runs. */
  debounceMs?: number;
  /** Hard ceiling on how long continuous changes may defer a save. */
  maxWaitMs?: number;
  /** Notified on every phase transition, for a save indicator. */
  onStatus?: (status: AutosaveStatus) => void;
  /** Injectable for tests. */
  now?: () => number;
}

const DEFAULT_DEBOUNCE_MS = 1200;
const DEFAULT_MAX_WAIT_MS = 8000;

export class AutosaveService {
  private readonly debounceMs: number;
  private readonly maxWaitMs: number;

  private dirty = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** When the current run of unsaved changes began — the anchor for `maxWaitMs`. */
  private dirtySince = 0;
  private inFlight: Promise<void> | null = null;
  private phase: AutosavePhase = 'clean';
  private savedAt: number | null = null;
  private error: string | null = null;
  private disposed = false;

  constructor(private readonly deps: AutosaveServiceDeps) {
    this.debounceMs = Math.max(0, deps.debounceMs ?? DEFAULT_DEBOUNCE_MS);
    this.maxWaitMs = Math.max(this.debounceMs, deps.maxWaitMs ?? DEFAULT_MAX_WAIT_MS);
  }

  /** Whether anything is unwritten — pending changes or a write still in flight. */
  get hasUnsavedChanges(): boolean {
    return this.dirty || this.inFlight !== null;
  }

  get status(): AutosaveStatus {
    return { phase: this.phase, savedAt: this.savedAt, error: this.error };
  }

  /** The document changed. Schedules a save; repeated calls collapse into one. */
  markDirty(): void {
    if (this.disposed) return;
    if (!this.dirty) {
      this.dirty = true;
      this.dirtySince = this.now();
    }
    this.setPhase('pending');
    // Re-armed on every change: the quiet period restarts, so a continuous drag writes once when it
    // settles rather than once per event — bounded by `maxWaitMs` for a drag that never settles.
    this.reschedule();
  }

  /**
   * Forget any pending changes without saving — the document was replaced (opened, imported, or a
   * new site), so what was pending belonged to a document that is no longer on screen.
   */
  markClean(): void {
    this.dirty = false;
    this.clearTimer();
    this.error = null;
    this.setPhase(this.savedAt === null ? 'clean' : 'saved');
  }

  /**
   * Save now if anything is pending, and resolve when the store has it.
   *
   * Used by the explicit Save command and by the tab-hidden hook, where the debounce is exactly the
   * wrong thing to honour. Retries a failed save, since the caller is asking again.
   */
  async flush(): Promise<void> {
    this.clearTimer();
    // A write already running may predate the newest changes, so wait it out and re-check.
    while (this.inFlight) await this.inFlight;
    if (!this.dirty || this.disposed) return;
    await this.run();
  }

  /** Stop the timer and drop the pending change. Any in-flight write is left to finish. */
  dispose(): void {
    this.disposed = true;
    this.dirty = false;
    this.clearTimer();
  }

  private schedule(): void {
    if (this.timer !== null || this.inFlight !== null || this.disposed) return;
    // Never push the save past the ceiling, however long the burst of changes runs.
    const deadline = this.dirtySince + this.maxWaitMs;
    const delay = Math.max(0, Math.min(this.debounceMs, deadline - this.now()));
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delay);
  }

  /** Re-arm the debounce from scratch, so this change starts a fresh quiet period. */
  private reschedule(): void {
    if (this.inFlight !== null) return; // The write in flight re-arms on completion.
    this.clearTimer();
    this.schedule();
  }

  private async run(): Promise<void> {
    if (!this.dirty || this.inFlight !== null || this.disposed) return;

    if (this.deps.shouldSave?.() === false) {
      // Not worth writing. The change is dropped rather than deferred, and the indicator says
      // nothing rather than claiming a save that never happened.
      this.dirty = false;
      this.clearTimer();
      this.setPhase(this.savedAt === null ? 'clean' : 'saved');
      return;
    }

    // Cleared before the write, so a change arriving *during* it is not mistaken for saved.
    this.dirty = false;
    this.setPhase('saving');

    const attempt = (async () => {
      try {
        await this.deps.save();
        this.savedAt = this.now();
        this.error = null;
      } catch (cause) {
        // Put the change back: it is still unwritten, and the unload guard must keep warning.
        this.dirty = true;
        this.dirtySince = this.now();
        this.error = cause instanceof Error ? cause.message : String(cause);
      }
    })();

    this.inFlight = attempt;
    await attempt;
    this.inFlight = null;

    if (this.error !== null) {
      // Deliberately not retried on a timer: a failing store (quota, blocked storage) would fail
      // every few seconds forever. The next edit or an explicit Save tries again.
      this.setPhase('error');
      return;
    }
    if (this.dirty) {
      this.dirtySince = this.now();
      this.setPhase('pending');
      this.reschedule();
      return;
    }
    this.setPhase('saved');
  }

  private setPhase(phase: AutosavePhase): void {
    // 'saving' and 'error' are always announced; the rest only on a real transition.
    if (phase === this.phase && phase !== 'saving' && phase !== 'error') return;
    this.phase = phase;
    this.deps.onStatus?.(this.status);
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  private now(): number {
    return this.deps.now?.() ?? Date.now();
  }
}
